import { useMemo, useState } from 'react';
import { ChartChangepointMarker } from '@elementary/common-ui/components';
import { Metrics } from '@elementary/common-ui/types';
import { CloudTestFeedback } from 'shared/types';
import { ExistingChangepoint, FeedbackTest } from '../types';
import {
  getChangepointMarkerLabel,
  getChangepointTrackingProperties,
  getMetricsWindow,
  isSameBucketStart,
} from '../utils';
import useFeedbackTracking from './useFeedbackTracking';
import useCloudTestFeedbacks from './useCloudTestFeedbacks';

const getLabel = (feedback: CloudTestFeedback) =>
  getChangepointMarkerLabel(feedback.created_by, feedback.created_at);

const useChangepointMarkers = ({
  test,
  resultId,
  metrics,
  canEdit,
  canGiveFeedback,
}: {
  test?: FeedbackTest;
  resultId: string;
  metrics?: Metrics[];
  canEdit: boolean;
  canGiveFeedback: boolean;
}) => {
  const [changepointToRemove, setChangepointToRemove] = useState<string>();
  const { start, end } = useMemo(
    () => getMetricsWindow(metrics ?? []),
    [metrics],
  );
  const hasUsedFeedback = !!metrics?.some(
    (metric) => metric.used_feedback_ids?.length,
  );
  const isEnabled = !!test && (hasUsedFeedback || canGiveFeedback);
  const { data } = useCloudTestFeedbacks({
    testId: test?.id ?? '',
    start,
    end,
    enabled: isEnabled && !!metrics?.length,
  });
  const track = useFeedbackTracking({
    test: test ?? { id: '', name: '', type: '' },
    resultId,
  });

  const activeChangepoints = useMemo(
    () =>
      (data?.feedbacks ?? []).filter(
        (feedback) => feedback.feedback_type === 'EXPECTED_CHANGE_POINT',
      ),
    [data],
  );

  const markers = useMemo<ChartChangepointMarker[]>(() => {
    const byId = new Map(
      activeChangepoints.map((feedback) => [feedback.id, feedback]),
    );
    return (metrics ?? []).flatMap((metric) => {
      const feedback = metric.used_feedback_ids
        ?.map((id) => byId.get(id))
        .find(Boolean);
      if (!feedback || !metric.bucket_start) return [];
      const trackingProperties = getChangepointTrackingProperties(feedback.id);
      return [
        {
          bucketStart: metric.bucket_start,
          label: getLabel(feedback),
          onTooltipOpen: () =>
            track('CHANGEPOINT_MARKER_TOOLTIP_VIEWED', trackingProperties),
          onRemoveClick: canEdit
            ? () => {
                track('CHANGEPOINT_REMOVE_CLICKED', trackingProperties);
                setChangepointToRemove(feedback.id);
              }
            : undefined,
        },
      ];
    });
  }, [activeChangepoints, metrics, canEdit, track]);

  // The picker shows every active changepoint, including ones added after this run.
  const pickerChangepoints = useMemo(
    () =>
      (metrics ?? []).flatMap((metric) => {
        const bucketStart = metric.bucket_start;
        const feedback =
          bucketStart &&
          activeChangepoints.find((changepoint) =>
            isSameBucketStart(changepoint.bucket_start, bucketStart),
          );
        if (!bucketStart || !feedback) return [];
        return [{ bucketStart, feedback }];
      }),
    [activeChangepoints, metrics],
  );

  const pickerMarkers = useMemo<ChartChangepointMarker[]>(
    () =>
      pickerChangepoints.map(({ bucketStart, feedback }) => ({
        bucketStart,
        label: getLabel(feedback),
      })),
    [pickerChangepoints],
  );

  const existingChangepoints = useMemo<ExistingChangepoint[]>(
    () =>
      pickerChangepoints.map(({ feedback }) => ({
        id: feedback.id,
        bucketStart: feedback.bucket_start,
        createdBy: feedback.created_by,
        createdAt: feedback.created_at,
      })),
    [pickerChangepoints],
  );

  return {
    markers: isEnabled ? markers : undefined,
    pickerMarkers: isEnabled ? pickerMarkers : undefined,
    existingChangepoints: isEnabled ? existingChangepoints : undefined,
    changepointToRemove,
    closeRemoveDialog: () => setChangepointToRemove(undefined),
    track,
  };
};

export default useChangepointMarkers;
